import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, mensagemDoErro, qs } from '../api';
import { useApi } from '../hooks';
import type { ItemAuditoria, UsuarioAdmin } from '../types';
import { Carregando, ErroForm } from '../ui';
import { dataHora } from './comum';
import { CampoData } from '../componentes/Campos';

const ENTIDADES = ['usuario', 'secretaria', 'projeto', 'acao', 'pesquisa', 'sessao'];
const POR_PAGINA = 50;

/** Nomes em português para as ações registradas; o que não estiver aqui aparece como veio. */
const ROTULO_ACAO: Record<string, string> = {
  'auth.login': 'Entrou no sistema',
  'auth.login_falhou': 'Tentativa de login falhou',
  'auth.login_bloqueado': 'Login recusado: conta bloqueada',
  'auth.conta_bloqueada': 'Conta bloqueada por tentativas',
  'auth.trocar_senha': 'Trocou a senha',
  'auth.inspecao_iniciar': 'Começou a inspecionar a conta de um usuário',
  'auth.inspecao_encerrar': 'Terminou de inspecionar a conta',
  'dados.carregar': 'Atualizou dados oficiais',
  'dados.importar': 'Enviou planilha de dados oficiais',
  'dados.remover': 'Apagou dados oficiais de um ano',
  'auth.refresh_reutilizado': 'Sessão suspeita derrubada',
  'usuario.criar': 'Criou usuário',
  'usuario.editar': 'Editou usuário',
  'usuario.resetar_senha': 'Gerou nova senha temporária',
  'usuario.encerrar_sessoes': 'Encerrou sessões',
  'secretaria.criar': 'Criou secretaria',
  'secretaria.editar': 'Editou secretaria',
  'projeto.criar': 'Criou projeto',
  'projeto.editar': 'Editou projeto',
  'acao.criar': 'Criou ação',
  'acao.editar': 'Editou ação',
  'acao.atualizar': 'Registrou andamento',
  'acao.importar_csv': 'Importou ações por CSV',
  'acao.exportar': 'Exportou ações',
  'arquivo.enviar': 'Anexou arquivo',
  'arquivo.excluir': 'Excluiu arquivo',
  'pesquisa.criar': 'Criou pesquisa',
  'pesquisa.editar': 'Editou pesquisa',
  'pesquisa.metas': 'Definiu metas da pesquisa',
  'pesquisa.abrir': 'Abriu pesquisa',
  'pesquisa.encerrar': 'Encerrou pesquisa',
  'resposta.anular': 'Anulou resposta de pesquisa',
};

const inicioDoDia = (d: string) => `${d}T00:00:00-03:00`;
const fimDoDia = (d: string) => `${d}T23:59:59.999-03:00`;

export function AuditoriaPage() {
  const usuarios = useApi<UsuarioAdmin[]>('/usuarios');
  const nomePor = useMemo(() => new Map((usuarios.dados ?? []).map((u) => [u.id, u.nome])), [usuarios.dados]);

  const [entidade, setEntidade] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [itens, setItens] = useState<ItemAuditoria[]>([]);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState<string | null>(null);
  const geracao = useRef(0);

  async function carregar(antesDe: string | null) {
    const minha = ++geracao.current;
    setCarregando(true);
    setErro('');
    try {
      const r = await api<ItemAuditoria[]>(
        `/auditoria${qs({ entidade, usuarioId, de: de ? inicioDoDia(de) : '', ate: ate ? fimDoDia(ate) : '', limite: POR_PAGINA, antesDe })}`,
      );
      if (minha !== geracao.current) return;
      setItens((atual) => (antesDe ? [...atual, ...r] : r));
      setTemMais(r.length === POR_PAGINA);
    } catch (e) {
      if (minha === geracao.current) setErro(mensagemDoErro(e));
    } finally {
      if (minha === geracao.current) setCarregando(false);
    }
  }

  useEffect(() => {
    void carregar(null);
  }, [entidade, usuarioId, de, ate]);

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Auditoria</h1>
          <p class="dica">Registro de quem fez o quê. Não pode ser editado nem apagado. Senhas nunca são gravadas aqui.</p>
        </div>
      </header>

      <section class="barra-filtros" aria-label="Filtros">
        <label class="campo"><span>Tipo</span>
          <select value={entidade} onChange={(e) => setEntidade(e.currentTarget.value)}>
            <option value="">Todos</option>
            {ENTIDADES.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </label>
        <label class="campo"><span>Pessoa</span>
          <select value={usuarioId} onChange={(e) => setUsuarioId(e.currentTarget.value)}>
            <option value="">Todas</option>
            {(usuarios.dados ?? []).map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </label>
        <label class="campo"><span>De</span><CampoData value={de} onChange={setDe} /></label>
        <label class="campo"><span>Até</span><CampoData value={ate} onChange={setAte} /></label>
        {(entidade || usuarioId || de || ate) && <button type="button" class="link" onClick={() => { setEntidade(''); setUsuarioId(''); setDe(''); setAte(''); }}>Limpar filtros</button>}
      </section>

      <ErroForm texto={erro} />

      <div class="tabela-rolagem">
        <table class="tabela">
          <caption class="so-leitor">Registro de auditoria</caption>
          <thead><tr><th scope="col">Quando</th><th scope="col">Quem</th><th scope="col">O quê</th><th scope="col">Sobre</th><th scope="col">IP</th><th scope="col"><span class="so-leitor">Detalhes</span></th></tr></thead>
          <tbody>
            {itens.map((i) => (
              <>
                <tr key={i.id}>
                  <td>{dataHora(i.em)}</td>
                  <td>{i.usuarioId ? (nomePor.get(i.usuarioId) ?? 'Usuário removido') : <span class="dica">— (não identificado)</span>}</td>
                  <td>{ROTULO_ACAO[i.acao] ?? i.acao}</td>
                  <td><small>{i.entidade}</small></td>
                  <td><small>{i.ip ?? '—'}</small></td>
                  <td>{(i.antes || i.depois) ? <button type="button" class="link" aria-expanded={aberto === i.id} onClick={() => setAberto(aberto === i.id ? null : i.id)}>{aberto === i.id ? 'Ocultar' : 'Detalhes'}</button> : null}</td>
                </tr>
                {aberto === i.id && (
                  <tr key={`${i.id}-d`} class="linha-detalhe">
                    <td colSpan={6}>
                      <div class="diff">
                        {i.antes != null && <div><strong>Antes</strong><pre>{JSON.stringify(i.antes, null, 2)}</pre></div>}
                        {i.depois != null && <div><strong>Depois</strong><pre>{JSON.stringify(i.depois, null, 2)}</pre></div>}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {carregando && itens.length === 0 && <Carregando />}
        {!carregando && itens.length === 0 && !erro && <div class="vazio-grande"><p>Nenhum registro com esses filtros.</p></div>}
      </div>

      <footer class="rodape-lista">
        <span class="dica">{itens.length} registro{itens.length === 1 ? '' : 's'} exibido{itens.length === 1 ? '' : 's'}</span>
        {temMais && <button type="button" class="botao" disabled={carregando} onClick={() => void carregar(itens[itens.length - 1]?.id ?? null)}>{carregando ? 'Carregando…' : 'Carregar mais'}</button>}
      </footer>
    </main>
  );
}
